'use client';

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useDictionary } from "@/core/hooks/use-dictionary";
import TextInput from "@/components/account/input/TextInput";
import { LoginFormProps } from "@/core/types/auth";
import { getLoginFormSchema, LoginFormData } from "@/core/schemas/authSchema";
import CaptchaField from "@/components/main/login/CaptchaField";
import { registerStepOne } from "@/core/lib/api/main/auth";

interface ExtendedLoginFormProps extends LoginFormProps {
    captcha_active: boolean;
    setStep: (step: 1 | 2 | 3) => void;
}

export default function NumberStepLoginForm({
    captcha_active,
    setMobile,
    setSessionToken,
    setRememberMe,
    mobile,
    rememberMe,
    setStep,
}: ExtendedLoginFormProps) {
    const { dictionary } = useDictionary();
    const loginSchema = getLoginFormSchema(dictionary);

    const {
        register,
        handleSubmit,
        setValue,
        setError,
        control,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            mobile,
            remember_me: rememberMe,
            recaptcha_token: captcha_active ? "" : "empty",
        },
    });

    const onSubmit = async (data: LoginFormData) => {
        setMobile(data.mobile);
        setRememberMe(data.remember_me);

        try {
            const result = await registerStepOne(
                data.mobile,
                data.recaptcha_token,
                data.remember_me
            );

            if (result.status?.toLowerCase() === "success") {
                setStep(result.data.step);
                setSessionToken(result.data.session_token);
            } else {
                setError("mobile", {
                    type: "server",
                    message: result.data?.mobile ? result.data.mobile : result.message,
                });
            }
        } catch (error) {
            console.error("Login error:", error);
            setError("mobile", { type: "client", message: dictionary.common?.error });
        }
    };

    const onCaptchaChange = (token: string | null) => {
        setValue("recaptcha_token", token || "");
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col mb-8 gap-4">
                <div className="min-h-[68px]">
                    <TextInput
                        label=""
                        placeholder={dictionary.forms?.mobile}
                        register={register}
                        inputClassName="w-full h-12 rounded-2xl border border-primary-300 bg-primary-0 px-6 text-center placeholder:text-primary-500 transition-colors focus:border-primary-900 focus:ring-4 focus:ring-primary-900/5 focus:outline-none"
                        className="gap-0"
                        name="mobile"
                        errors={errors}
                        variant="default"
                    />
                </div>
                <Controller
                    name="remember_me"
                    control={control}
                    render={({ field }) => (
                        <div className="flex gap-x-2 items-center justify-center text-[13px] text-primary-600">
                            <Checkbox
                                className="cursor-pointer m-0"
                                id="remember_me"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                            <label htmlFor="remember_me" className="select-none">
                                {dictionary.forms?.rememberme}
                            </label>
                        </div>
                    )}
                />
                {captcha_active && (
                    <CaptchaField
                        error={errors.recaptcha_token?.message}
                        onChange={onCaptchaChange}
                    />
                )}
            </div>

            <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full h-12 rounded-2xl bg-primary-900 text-primary-0 text-[13px] uppercase tracking-[0.14em] hover:bg-primary-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
                {isSubmitting ? (
                    <Loader2 className="animate-spin size-4" />
                ) : (
                    <>
                        {dictionary.forms?.next}
                        <ArrowRight className="size-3.5 rtl:rotate-180 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
                    </>
                )}
            </button>
        </form>
    );
}